/**
 * StatusPage — public uptime & service-health page (/status)
 * ──────────────────────────────────────────────────────────
 * Polls the public GET /api/status endpoint every 30s and renders a
 * Stripe/Linear-style status board with traffic-light indicators per
 * sub-system + latency. Auto-refreshes; works fully RTL.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  CheckCircle2, AlertTriangle, XCircle, Loader2, ArrowLeft, RefreshCw,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  operational: { ar: 'تعمل بشكل طبيعي', en: 'Operational',     dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', Icon: CheckCircle2 },
  degraded:    { ar: 'أداء منخفض',       en: 'Degraded',        dot: 'bg-amber-500',  text: 'text-amber-700',   bg: 'bg-amber-50',  border: 'border-amber-200',  Icon: AlertTriangle },
  down:        { ar: 'انقطاع',           en: 'Major outage',    dot: 'bg-rose-500',   text: 'text-rose-700',    bg: 'bg-rose-50',   border: 'border-rose-200',   Icon: XCircle },
};

const fmtMs = (ms) => (ms == null ? '—' : `${ms} ms`);

const StatusPage = () => {
  const { isRTL, direction } = useLanguage();
  const t = (ar, en) => (isRTL ? ar : en);

  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/api/status`);
      setSnap(data);
    } catch {
      setSnap({ status: 'down', services: [], checked_at: new Date().toISOString(), uptime_pct_30d: 0, incidents: [] });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const overall = STATUS_META[snap?.status || 'operational'] || STATUS_META.operational;

  return (
    <div className={`min-h-screen bg-[#f7f6f3] ${isRTL ? 'font-arabic' : ''}`} dir={direction} data-testid="status-page">
      {/* Header */}
      <header className="border-b border-[rgba(20,15,10,0.06)] bg-white">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-600 hover:text-flame-600" data-testid="status-home-link">
            <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            {t('العودة إلى الموقع', 'Back to site')}
          </Link>
          <img src={`${API}/api/media/varotrade_official_emblem.png`} alt="VAROTRADES" className="h-8 w-auto" />
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-10 sm:py-16 max-w-3xl">
        {/* Overall banner */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className={`rounded-3xl border ${overall.border} ${overall.bg} p-6 sm:p-8 text-center shadow-sm`}
          data-testid="status-overall-banner"
        >
          <div className="varo-eyebrow text-ink-500 mb-2">{t('حالة الخدمة', 'System status')}</div>
          <div className="inline-flex items-center gap-2.5 justify-center mb-1">
            <span className={`w-3 h-3 rounded-full ${overall.dot} animate-pulse`} />
            <h1 className={`text-2xl sm:text-3xl font-black tracking-tight ${overall.text}`}>
              {loading ? <Loader2 className="w-7 h-7 animate-spin inline" /> : (isRTL ? overall.ar : overall.en)}
            </h1>
          </div>
          <p className="text-sm text-ink-600 mt-2">
            {snap?.checked_at && (
              <>
                {t('آخر فحص:', 'Last checked:')} {new Date(snap.checked_at).toLocaleTimeString()} ·{' '}
                {t('وقت التشغيل خلال 30 يوماً:', 'Uptime over 30 days:')} <strong>{snap?.uptime_pct_30d?.toFixed(2)}%</strong>
              </>
            )}
          </p>
          <button
            onClick={load}
            disabled={refreshing}
            className="mt-4 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-white border border-[rgba(20,15,10,0.12)] text-ink-700 hover:bg-ink-50 text-[13px] font-semibold disabled:opacity-50"
            data-testid="status-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </button>
        </motion.div>

        {/* Services */}
        <section className="mt-8 rounded-3xl border border-[rgba(20,15,10,0.06)] bg-white p-2 sm:p-4 shadow-sm">
          <h2 className="px-3 sm:px-4 pt-3 pb-2 text-[11px] uppercase tracking-[0.06em] text-ink-500 font-semibold">
            {t('الخدمات', 'Services')}
          </h2>
          <ul className="divide-y divide-[rgba(20,15,10,0.05)]">
            {(snap?.services || []).map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.operational;
              return (
                <li key={s.name} className="flex items-center gap-3 px-3 sm:px-4 py-3.5" data-testid={`status-service-${s.name.replace(/\s+/g, '-').toLowerCase()}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink-900 text-[14px] truncate">{s.name}</div>
                    {s.error && <div className="text-[11px] text-rose-600 truncate mt-0.5">{s.error}</div>}
                  </div>
                  <div className="text-[11px] font-mono text-ink-400">{fmtMs(s.latency_ms)}</div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${meta.bg} ${meta.text} border ${meta.border}`}>
                    <meta.Icon className="w-3 h-3" />
                    {isRTL ? meta.ar : meta.en}
                  </span>
                </li>
              );
            })}
            {loading && (
              <li className="px-4 py-10 text-center text-ink-400 text-sm">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> {t('جارٍ التحقق...', 'Checking...')}
              </li>
            )}
          </ul>
        </section>

        {/* Incidents */}
        <section className="mt-6 rounded-3xl border border-[rgba(20,15,10,0.06)] bg-white p-6 shadow-sm" data-testid="status-incidents-card">
          <h2 className="font-bold text-ink-900 text-base mb-3">{t('الحوادث الأخيرة', 'Recent incidents')}</h2>
          {(snap?.incidents || []).length === 0 ? (
            <div className="text-sm text-ink-500 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              {t('لا توجد حوادث في آخر 30 يوماً.', 'No incidents reported in the last 30 days.')}
            </div>
          ) : (
            <ul className="space-y-2">
              {snap.incidents.map((inc, idx) => (
                <li key={idx} className="text-sm">
                  <span className="font-semibold text-ink-900">{inc.title}</span>{' '}
                  <span className="text-ink-500">— {new Date(inc.ts).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="text-center text-[11px] text-ink-400 mt-8">
          {t('للحوادث الجارية أو الأسئلة، تواصل مع', 'For ongoing incidents or questions, contact')}{' '}
          <a href="mailto:support@varotrades.com" className="text-flame-600 hover:underline">support@varotrades.com</a>
        </p>
      </main>
    </div>
  );
};

export default StatusPage;
